import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { map } from 'lodash';
import {
  OnboardingStep,
  OnboardingTaskStatusEnum,
  VerifiedIdOrCodeStatusEnum,
} from '@nxsys/constants/AppEnums';
import AddNationality from './AddNationality';
import AddID from './AddID';
import AddShareCode from './AddShareCode';
import AddDOB from './AddDOB';
import AddBankDetail from './AddBankDetail';
import AddAddressDetail from './AddAddressDetail';
import AddJobTitle from './AddJobTitle';
import AddWorkingTimeRegulation from './AddWorkingTimeRegulation';
import AddAgencyConductRegulation from './AddAgencyConductRegulation';
import AddP45StarterDeclaration from './AddP45StarterDeclaration';
import AddP45Details from './AddP45Details';
import AddNINumber from './AddNINumber';
import AddHolidayPayMethod from './AddHolidayPayMethod';
import VerifyContact from './VerifyContact';
import AddUTRNumber from './AddUTRNumber';
import AddCaptureIDDocs from './AddCaptureIDDocs';
import StepStateContext from './StepStateContext';
import resolveOnboardingSettings from './resolveOnboardingSettings';
import { OnboardingPayload, Step } from './useOnboardingSteps';

export type SubmitStep = (
  stepKey: string,
  values: any,
  options?: { hideToast?: boolean; skipComplete?: boolean },
) => void;

type OnboardingStepListProps = {
  data: OnboardingPayload;
  steps: Record<string, Step>;
  nationality: string | null;
  onChangeNationality: (value: string) => void;
  onSubmitStep: SubmitStep;
  /**
   * Whether the person filling this in is the employee themselves.
   *
   * Two steps can only be driven by the employee, so both are disabled for an
   * admin completing the onboarding on a prospect's behalf:
   *   - Yoti ID check (addID) — a live camera scan on the employee's device
   *   - Zoho sign contract (signContract) — the endpoints are scoped to the
   *     signed-in user, so an admin would act on their own contract
   * The rest of each step still works for the admin (birth-certificate upload,
   * the no-passport checkbox, marking the contract step completed).
   */
  isSelfService?: boolean;
  /** Remount key — resets each step's local form state after a save. */
  refreshKey?: number;
  /**
   * Employee onboarding must be completed in order: every step after the first
   * incomplete one is locked until that one is saved. The admin flow leaves
   * this off and can save any step at any time.
   */
  sequential?: boolean;
  /** Locks every step — used once the whole onboarding is completed. */
  readOnly?: boolean;
  /** Key of the step whose save is in flight, so its button can show loading. */
  submittingStepKey?: string | null;
  /** Reloads the onboarding payload, e.g. once a Yoti session settles. */
  onRefetchOnboarding?: () => void;
};

/**
 * Renders the onboarding step cards for a payload. Used by both the admin modal
 * and the employee page; the only difference between them is which API each
 * step submit hits, which the parent supplies via `onSubmitStep`.
 */
const OnboardingStepList: React.FC<OnboardingStepListProps> = ({
  data,
  steps,
  nationality,
  onChangeNationality,
  onSubmitStep,
  isSelfService = false,
  refreshKey = 0,
  sequential = false,
  readOnly = false,
  submittingStepKey = null,
  onRefetchOnboarding,
}) => {
  const { messages } = useIntl();
  const prospect = data?.prospectOnboarding;
  const onboarding = data?.onboarding;
  const onboardingTasks = data?.prospectOnboarding?.onboardingTasks;
  // The employer settings this onboarding was invited under, not the live
  // company row — see resolveOnboardingSettings.
  const onboardingSettings = useMemo(
    () => resolveOnboardingSettings(prospect),
    [prospect],
  );

  // The first step not yet completed — the one to work on next. It opens
  // expanded in both flows; in sequential mode everything after it is also
  // locked. Completed steps stay reachable so an earlier answer can be fixed.
  const activeIndex = useMemo(() => {
    const incomplete = Object.values(steps)
      .filter((step) => !step.completed)
      .map((step) => step.index);
    return incomplete.length ? Math.min(...incomplete) : Infinity;
  }, [steps]);

  const lockedHint = (readOnly
    ? (messages['prospect.onboarding.onboardingCompleted'] ??
      'Onboarding is already completed')
    : (messages['prospect.onboarding.stepLocked'] ??
      'Complete the previous step first')) as string;

  const getStepContent = (step: Step) => {
    const { key: stepKey, completed, index } = step;
    const stepNumber = index + 1;
    switch (stepKey) {
      case OnboardingStep.ADD_NATIONALITY:
        return (
          <AddNationality
            stepNumber={stepNumber}
            nationality={nationality}
            onChangeNationality={onChangeNationality}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_ID:
        return (
          <AddID
            stepNumber={stepNumber}
            havePassport={Boolean(onboarding?.havePassport ?? true)}
            verifiedIdOrCodeStatus={
              onboarding?.verifiedIdOrCodeStatus as VerifiedIdOrCodeStatusEnum
            }
            documentCopyBirthcertificate={
              onboarding?.documentCopyBirthcertificate
            }
            ukVerifiedIdMethod={onboardingSettings.UKVerifiedIdMethod}
            yotiSessionId={onboarding?.sessionIdYoti}
            yotiSessionState={onboarding?.yotiSessionState}
            onRefetch={onRefetchOnboarding}
            onboardingCompleted={readOnly}
            disabledVerify={!isSelfService}
            completed={completed}
            onSubmitStep={(values, options) => {
              onSubmitStep(stepKey, values, options);
            }}
          />
        );
      case OnboardingStep.ADD_SHARE_CODE:
        return (
          <AddShareCode
            stepNumber={stepNumber}
            shareCode={onboarding?.shareCode}
            haveShareCode={Boolean(onboarding?.haveShareCode ?? true)}
            verifiedIdOrCodeStatus={
              onboarding?.verifiedIdOrCodeStatus as VerifiedIdOrCodeStatusEnum
            }
            documentID={onboarding?.documentID}
            completed={completed}
            onSubmitStep={(values, options) => {
              onSubmitStep(stepKey, values, options);
            }}
          />
        );
      case OnboardingStep.ADD_DOB:
        return (
          <AddDOB
            stepNumber={stepNumber}
            DOB={onboarding?.DOB}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_BANK_DETAILS:
        return (
          <AddBankDetail
            stepNumber={stepNumber}
            accountName={onboarding?.bankDetails?.accountName}
            accountNumber={onboarding?.bankDetails?.accountNumber}
            sortCode={onboarding?.bankDetails?.sortCode}
            bankName={onboarding?.bankDetails?.bankName}
            buildingSocietyNumber={
              onboarding?.bankDetails?.buildingSocietyNumber
            }
            IBANReference={onboarding?.bankDetails?.IBANReference}
            SWIFTNo={onboarding?.bankDetails?.SWIFTNo}
            country={onboarding?.bankDetails?.country}
            isInternalAccount={onboarding?.bankDetails?.isInternalAccount}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_ADDRESS_DETAILS:
        return (
          <AddAddressDetail
            stepNumber={stepNumber}
            address1={onboarding?.addressDetails?.address?.[0]}
            address2={onboarding?.addressDetails?.address?.[1]}
            address3={onboarding?.addressDetails?.address?.[2]}
            city={onboarding?.addressDetails?.city}
            county={onboarding?.addressDetails?.county}
            country={onboarding?.addressDetails?.country}
            postCode={onboarding?.addressDetails?.postCode}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_JOB_DESCRIPTION:
        return (
          <AddJobTitle
            stepNumber={stepNumber}
            jobTitle={onboarding?.jobDescription}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_WORKING_TIME_REGULATION:
        return (
          <AddWorkingTimeRegulation
            stepNumber={stepNumber}
            workingTimeRegulation={onboarding?.workingTimeRegulation}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION:
        return (
          <AddAgencyConductRegulation
            stepNumber={stepNumber}
            agencyConductRegulation={onboarding?.agencyConductRegulation}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_P45_STARTER_DECLARATION:
        return (
          <AddP45StarterDeclaration
            stepNumber={stepNumber}
            p45StarterDeclaration={onboarding?.p45StarterDeclaration}
            taxCode={onboarding?.taxCode}
            taxBasis={onboarding?.taxBasis}
            // One-way coupling: the preceding P45 Details step decides whether
            // the tax code / basis may be edited here.
            haveStepP45Details={
              onboardingTasks?.addP45Details !== OnboardingTaskStatusEnum.NA
            }
            starterType={onboarding?.p45Details?.starterType}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_P45_DETAILS:
        return (
          <AddP45Details
            stepNumber={stepNumber}
            starterType={onboarding?.p45Details?.starterType}
            p45GrossToDate={onboarding?.p45Details?.p45GrossToDate}
            p45TaxToDate={onboarding?.p45Details?.p45TaxToDate}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_NI_NUMBER:
        return (
          <AddNINumber
            stepNumber={stepNumber}
            NINumber={onboarding?.NINumber}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_HOLIDAY_PAY_METHOD:
        return (
          <AddHolidayPayMethod
            stepNumber={stepNumber}
            holidayPaymentMethod={onboarding?.holidayPayMethod}
            paymentModel={data?.prospectOnboarding?.paymentModel}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_SIGN_CONTRACT:
        return (
          <VerifyContact
            stepNumber={stepNumber}
            markedAsCompleted={
              onboardingTasks?.signContract === OnboardingTaskStatusEnum.YES
            }
            zohoSignStatus={prospect?.zohoSignStatus}
            zohoRequestId={prospect?.zohoRequestId}
            zohoDocumentId={prospect?.zohoDocumentId}
            zohoDocumentExpired={prospect?.zohoDocumentExpired}
            zohoDocumentDeleted={prospect?.zohoDocumentDeleted}
            isProgress={prospect?.isProgress}
            completed={completed}
            onSubmitStep={(values, options) => {
              onSubmitStep(stepKey, values, options);
            }}
            disabledConnect={!isSelfService}
          />
        );
      case OnboardingStep.ADD_UTR:
        return (
          <AddUTRNumber
            stepNumber={stepNumber}
            UTRNo={onboarding?.UTRNo}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      case OnboardingStep.ADD_CAPTURE_ID_DOCS:
        return (
          <AddCaptureIDDocs
            stepNumber={stepNumber}
            captureIdDocs={onboarding?.captureIdDocs}
            completed={completed}
            onSubmitStep={(values) => onSubmitStep(stepKey, values)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="OnboardingSteps flex flex-col gap-y-4" key={refreshKey}>
      {map(steps, (step) => (
        <StepStateContext.Provider
          key={step.key}
          value={{
            locked: readOnly || (sequential && step.index > activeIndex),
            lockedHint,
            submitting: submittingStepKey === step.key,
            // Open the first step still to be filled in — the next thing to do
            // in both flows. Skipped once everything is locked.
            autoExpand: !readOnly && step.index === activeIndex,
          }}
        >
          {getStepContent(step)}
        </StepStateContext.Provider>
      ))}
    </div>
  );
};

export default OnboardingStepList;
